import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowLeft, ChevronUp } from 'lucide-react';
import { useT } from '../i18n';
import { useAuthUser } from '../lib/auth';
import { TEAM_AVATARS } from './teamAvatars';

function ScrollTopButton({ expanded }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show || expanded) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
      className="pointer-events-auto absolute -top-14 right-1.5 w-11 h-11 rounded-full bg-white border border-neutral-200 shadow-lg text-neutral-500 hover:text-[#F77F11] hover:border-[#F77F11] flex items-center justify-center transition-colors"
      data-testid="scroll-top-btn"
    >
      <ChevronUp className="w-5 h-5" />
    </button>
  );
}

/* =================================================================
   AIConcierge 2.0 — Rachel as AI Concierge for the whole ecosystem.
   First objective: CLASSIFY the visitor (one click), then route the
   journey. Every classification emits VISITOR_CLASSIFIED + a specific
   event to the Event Bus and is visible in Mission Control.
   ================================================================= */

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '14085901102';
const COOKIE_KEY = 'kitxens_cookie_consent_v2';
const VISITOR_KEY = 'kx_visitor_type';

function cookiesDecided() {
  try {
    const stored = JSON.parse(localStorage.getItem(COOKIE_KEY) || 'null');
    return !!(stored && stored.set);
  } catch (e) {
    return false;
  }
}

function WhatsAppGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
    </svg>
  );
}

const wa = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

/* Dynamic flow: opened from the competitor comparator when the visitor is losing. */
function buildCompareFlow(isEs, ctx) {
  const you = ctx?.you || (isEs ? 'tu restaurante' : 'your restaurant');
  const rival = ctx?.rival || (isEs ? 'tu competidor' : 'your competitor');
  const score = ctx?.you_score != null && ctx?.rival_score != null ? ` (${ctx.you_score} vs ${ctx.rival_score})` : '';
  const tie = ctx?.verdict === 'tie';
  return {
    emoji: '🥊',
    label: isEs ? 'Plan de 90 días' : '90-day plan',
    msg: isEs
      ? (tie
        ? `Vi tu comparación: ${you} vs ${rival}${score} — empate técnico. Tengo un plan de 90 días para que tomes la delantera: SEO local, visibilidad en ChatGPT/Gemini y reseñas. Te lo explico en 2 minutos.`
        : `Vi tu comparación: ${rival} te está ganando${score}. Tranquilo — tenemos un plan de 90 días probado para adelantarles: SEO local, visibilidad en ChatGPT/Gemini y reseñas. Te lo explico en 2 minutos.`)
      : (tie
        ? `I saw your head-to-head: ${you} vs ${rival}${score} — a technical tie. I have a 90-day plan to take the lead: local SEO, ChatGPT/Gemini visibility and reviews. I'll walk you through it in 2 minutes.`
        : `I saw your head-to-head: ${rival} is beating you${score}. Don't worry — we have a proven 90-day plan to overtake them: local SEO, ChatGPT/Gemini visibility and reviews. I'll walk you through it in 2 minutes.`),
    ctas: [
      {
        label: isEs ? 'Ver mi plan de 90 días' : 'Get my 90-day plan',
        href: wa(isEs
          ? `Hola Rachel, acabo de comparar ${you}${ctx?.you_score != null ? ` (${ctx.you_score}/100)` : ''} contra ${rival}${ctx?.rival_score != null ? ` (${ctx.rival_score}/100)` : ''} y ${tie ? 'estamos empatados' : 'voy perdiendo'}. Quiero el plan de 90 días para adelantarles.`
          : `Hi Rachel, I just compared ${you}${ctx?.you_score != null ? ` (${ctx.you_score}/100)` : ''} against ${rival}${ctx?.rival_score != null ? ` (${ctx.rival_score}/100)` : ''} and ${tie ? "we're tied" : "I'm losing"}. I want the 90-day plan to beat them.`),
        whatsapp: true, primary: true,
      },
      { label: isEs ? 'Agendar demo gratis' : 'Book a free demo', to: '/demo' },
    ],
  };
}

/* Visitor journeys — message + CTAs per classified type. */
function buildFlows(isEs) {
  return {
    restaurant_owner: {
      emoji: '🍽', label: isEs ? 'Tengo un restaurante' : 'I own a restaurant',
      msg: isEs ? '¡Perfecto! Empecemos con una auditoría gratuita de tu restaurante — en minutos ves dónde estás perdiendo ventas.' : "Perfect! Let's start with a free audit of your restaurant — in minutes you'll see where you're losing sales.",
      ctas: [
        { label: isEs ? 'Auditoría Gratis' : 'Free Restaurant Audit', to: '/#audit', primary: true },
        { label: 'WhatsApp', href: wa(isEs ? 'Hola Rachel, tengo un restaurante y quiero hacer crecer mis ventas.' : "Hi Rachel, I own a restaurant and I'd like to grow my sales."), whatsapp: true },
      ],
    },
    affiliate: {
      emoji: '🤝', label: isEs ? 'Quiero ser Afiliado' : 'I want to become an Affiliate',
      msg: isEs ? 'Gana el 30% recurrente recomendando KITXENS a restaurantes — y tu referido recibe un 10% de descuento durante un año.' : 'Earn 30% recurring by referring KITXENS to restaurants — and your referral gets 10% off for a year.',
      ctas: [
        { label: isEs ? 'Ver programa y aplicar' : 'View program & apply', to: '/affiliates', primary: true },
        { label: 'WhatsApp', href: wa(isEs ? 'Hola Rachel, quiero unirme al programa de afiliados de KITXENS.' : 'Hi Rachel, I want to join the KITXENS affiliate program.'), whatsapp: true },
      ],
    },
    partner: {
      emoji: '⚡', label: isEs ? 'Partnership tecnológico' : "I'm interested in a Partnership",
      msg: isEs ? 'Exploramos integraciones, API, white-label y co-marketing. Cuéntanos qué tienes en mente.' : 'We explore integrations, API, white-label and co-marketing. Tell us what you have in mind.',
      ctas: [
        { label: isEs ? 'Hablar de partnership' : 'Discuss partnership', href: wa(isEs ? 'Hola, me interesa un partnership tecnológico con KITXENS.' : "Hi, I'm interested in a technology partnership with KITXENS."), whatsapp: true, primary: true },
        { label: isEs ? 'Contacto' : 'Contact', to: '/contact' },
      ],
    },
    knowledge: {
      emoji: '📚', label: isEs ? 'Explorar Recursos IA' : 'Explore AI Resources',
      msg: isEs ? 'Nuestro Knowledge Center publica inteligencia del sector cada semana. Te recomiendo empezar por el Magazine y la señal en vivo.' : 'Our Knowledge Center publishes sector intelligence every week. Start with the Magazine and the live intelligence feed.',
      ctas: [
        { label: 'Magazine', to: '/magazine', primary: true },
        { label: 'Live Intelligence', to: '/intelligence' },
      ],
    },
    support: {
      emoji: '💬', label: isEs ? 'Necesito Soporte' : 'I need Support',
      msg: isEs ? 'Estás en buenas manos. Cuéntame qué necesitas — POS, Index AI, agentes o tu cuenta — y lo resolvemos.' : "You're in good hands. Tell me what you need — POS, Index AI, agents or your account — and we'll sort it out.",
      ctas: [
        { label: isEs ? 'Abrir soporte' : 'Open support chat', href: wa(isEs ? 'Hola Rachel, soy cliente de KITXENS y necesito soporte.' : "Hi Rachel, I'm a KITXENS customer and I need support."), whatsapp: true, primary: true },
      ],
    },
    investor: {
      emoji: '💼', label: isEs ? 'Soy Inversor' : "I'm an Investor",
      msg: isEs ? 'Gracias por tu interés en KITXENS. Podemos compartir visión, modelo de negocio y crecimiento en una reunión.' : 'Thanks for your interest in KITXENS. We can share vision, business model and growth in a meeting.',
      ctas: [
        { label: isEs ? 'Solicitar reunión' : 'Request a meeting', href: wa(isEs ? 'Hola, soy inversor y me gustaría conocer más sobre KITXENS.' : "Hi, I'm an investor and I'd like to learn more about KITXENS."), whatsapp: true, primary: true },
        { label: isEs ? 'Contacto' : 'Contact', to: '/contact' },
      ],
    },
    media: {
      emoji: '📰', label: isEs ? 'Prensa / Media' : 'Media',
      msg: isEs ? 'Encantados de hablar con prensa: información de la empresa, entrevistas con el fundador y material de marca.' : 'Happy to talk to media: company information, founder interviews and brand assets.',
      ctas: [
        { label: isEs ? 'Contactar prensa' : 'Press inquiry', href: wa(isEs ? 'Hola, soy de prensa y me gustaría información sobre KITXENS.' : "Hi, I'm from the media and I'd like information about KITXENS."), whatsapp: true, primary: true },
      ],
    },
    other: {
      emoji: '👋', label: isEs ? 'Otro' : 'Other',
      msg: isEs ? '¿Qué te trae hoy a KITXENS? Escríbeme y te dirijo a la persona (o agente IA) correcta.' : 'What brings you to KITXENS today? Message me and I\'ll route you to the right person (or AI agent).',
      ctas: [
        { label: isEs ? 'Escribir a Rachel' : 'Message Rachel', href: wa(isEs ? 'Hola Rachel…' : 'Hi Rachel…'), whatsapp: true, primary: true },
      ],
    },
  };
}

async function classify(type, lang) {
  try {
    await fetch(`${API}/api/visitor/classify`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, page: window.location.pathname, referrer: document.referrer || null, lang }),
    });
  } catch { /* never block the UI */ }
}

export default function AIConcierge() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const reduceMotion = useReducedMotion();
  const rachel = TEAM_AVATARS[0];
  const { pathname } = useLocation();
  const authUser = useAuthUser();

  const [expanded, setExpanded] = useState(false); // start minimized (bubble) — user opens Rachel when ready
  const [ready, setReady] = useState(() => cookiesDecided());
  const [view, setView] = useState(() => localStorage.getItem(VISITOR_KEY) || 'menu');
  const [compareCtx, setCompareCtx] = useState(null);
  const collapseTimer = useRef(null);

  // Programmatic open with context (e.g., "90-day plan" button in the comparator).
  useEffect(() => {
    const onOpen = (e) => {
      clearTimeout(collapseTimer.current);
      if (e.detail?.context) setCompareCtx(e.detail.context);
      if (e.detail?.view) setView(e.detail.view);
      setReady(true);
      setExpanded(true);
    };
    window.addEventListener('kitxens:rachel-open', onOpen);
    return () => window.removeEventListener('kitxens:rachel-open', onOpen);
  }, []);

  useEffect(() => {
    if (ready) return undefined;
    const onCookieSet = () => setReady(true);
    window.addEventListener('kitxens:cookie-consent-set', onCookieSet);
    return () => window.removeEventListener('kitxens:cookie-consent-set', onCookieSet);
  }, [ready]);

  useEffect(() => {
    if (!ready) return undefined;
    collapseTimer.current = setTimeout(() => setExpanded(false), 10000);
    return () => clearTimeout(collapseTimer.current);
  }, [ready]);

  useEffect(() => {
    const onFormSubmitted = () => { clearTimeout(collapseTimer.current); setExpanded(false); };
    window.addEventListener('kitxens:form-submitted', onFormSubmitted);
    return () => window.removeEventListener('kitxens:form-submitted', onFormSubmitted);
  }, []);

  const flows = buildFlows(isEs);

  // Context-aware ordering: knowledge first on content pages.
  const order = ['restaurant_owner', 'affiliate', 'partner', 'knowledge', 'support', 'investor', 'media', 'other'];
  const onContent = pathname.startsWith('/magazine') || pathname.startsWith('/intelligence');
  const menuOrder = onContent ? ['knowledge', ...order.filter((k) => k !== 'knowledge')] : order;

  const pick = (type) => {
    clearTimeout(collapseTimer.current);
    localStorage.setItem(VISITOR_KEY, type);
    setView(type);
    classify(type, lang);
  };

  const resetMenu = () => {
    localStorage.removeItem(VISITOR_KEY);
    setView('menu');
  };

  const handleExpand = () => { clearTimeout(collapseTimer.current); setExpanded(true); };
  const handleCollapse = () => { clearTimeout(collapseTimer.current); setExpanded(false); };

  if (!ready) return null;
  const internal = pathname.startsWith('/admin') || pathname.startsWith('/affiliates/dashboard') || pathname === '/login';
  if (internal || authUser) return null;
  const flow = view === 'compare_plan'
    ? buildCompareFlow(isEs, compareCtx)
    : view !== 'menu' ? flows[view] || flows.other : null;

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] pointer-events-none" data-testid="ai-concierge">
      <ScrollTopButton expanded={expanded} />
      <style>{`
        @keyframes kx-concierge-pulse {
          0%   { transform: scale(1);   opacity: 0;    }
          4%   { transform: scale(1);   opacity: 0.35; }
          12%  { transform: scale(1.7); opacity: 0;    }
          100% { transform: scale(1.7); opacity: 0;    }
        }
        @keyframes kx-status-pulse {
          0%, 100% { opacity: 1;    box-shadow: 0 0 0 0   rgba(34,197,94,0.45); }
          50%      { opacity: 0.85; box-shadow: 0 0 0 5px rgba(34,197,94,0);    }
        }
        @media (prefers-reduced-motion: reduce) {
          .kx-fab-pulse, .kx-status-pulse { animation: none !important; }
        }
      `}</style>

      <AnimatePresence mode="wait" initial={false}>
        {expanded ? (
          <motion.div
            key="card"
            initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.96 }}
            animate={reduceMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto w-[300px] sm:w-[340px] rounded-2xl bg-white text-neutral-900 shadow-[0_30px_80px_-25px_rgba(15,17,23,0.45)] border border-neutral-200/80 overflow-hidden"
            role="dialog"
            aria-label="Rachel — AI Concierge"
            data-testid="concierge-card"
          >
            {/* Identity row */}
            <div className="flex items-start gap-3 px-5 pt-5">
              <div className="relative shrink-0">
                <div className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-white bg-orange-50 shadow-[0_6px_18px_-8px_rgba(15,17,23,0.4)]">
                  <img src={rachel.img} alt="Rachel" loading="lazy" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold tracking-[-0.01em] leading-tight text-neutral-900">Rachel</p>
                <p className="mt-0.5 text-[9.5px] tracking-[0.18em] uppercase font-bold text-[#F77F11]">AI Concierge</p>
                <div className="mt-1 flex items-center gap-1.5">
                  <span aria-hidden="true" className="kx-status-pulse inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" style={{ animation: 'kx-status-pulse 2.8s ease-in-out infinite' }} />
                  <span className="text-[10.5px] text-neutral-500" data-testid="concierge-status">{isEs ? 'En línea' : 'Online'}</span>
                </div>
              </div>
              <button
                type="button" onClick={handleCollapse} aria-label={isEs ? 'Cerrar' : 'Close'}
                className="-mr-1 -mt-1 w-7 h-7 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500 hover:text-neutral-900 transition-colors"
                data-testid="concierge-collapse"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <AnimatePresence mode="wait" initial={false}>
              {view === 'menu' ? (
                <motion.div key="menu" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.18 }} className="px-5 pb-5">
                  <p className="mt-3.5 text-[13.5px] font-semibold text-neutral-800">
                    👋 {isEs ? 'Bienvenido a KITXENS' : 'Welcome to KITXENS'}
                  </p>
                  <p className="mt-0.5 text-[12px] text-neutral-500">{isEs ? '¿En qué puedo ayudarte hoy?' : 'How can I help you today?'}</p>
                  <div className="mt-3 grid grid-cols-2 gap-1.5" data-testid="concierge-menu">
                    {menuOrder.map((k) => (
                      <button
                        key={k} type="button" onClick={() => pick(k)}
                        className="text-left rounded-xl border border-neutral-200 hover:border-[#F77F11]/60 hover:bg-orange-50/60 px-2.5 py-2 text-[11px] font-medium text-neutral-700 transition-colors leading-snug"
                        data-testid={`concierge-opt-${k}`}
                      >
                        <span className="mr-1">{flows[k].emoji}</span>{flows[k].label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div key={view} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} transition={{ duration: 0.18 }} className="px-5 pb-5" data-testid={`concierge-flow-${view}`}>
                  <p className="mt-3.5 text-[12px] text-neutral-600 leading-relaxed">
                    <span className="mr-1">{flow.emoji}</span>{flow.msg}
                  </p>
                  <div className="mt-3.5 flex flex-col gap-2">
                    {flow.ctas.map((c) =>
                      c.href ? (
                        <a
                          key={c.label} href={c.href} target="_blank" rel="noopener noreferrer"
                          className={`inline-flex items-center justify-center gap-2 h-10 rounded-full text-[12.5px] font-semibold transition-colors ${
                            c.whatsapp ? 'bg-[#25D366] hover:bg-[#1fb955] text-white shadow-[0_10px_24px_-10px_rgba(37,211,102,0.6)]'
                              : c.primary ? 'bg-[#F77F11] hover:bg-[#d8651a] text-white' : 'border border-neutral-300 text-neutral-700 hover:border-neutral-500'
                          }`}
                          data-testid={`concierge-cta-${view}-${c.whatsapp ? 'wa' : 'link'}`}
                        >
                          {c.whatsapp && <WhatsAppGlyph className="w-[14px] h-[14px]" />}{c.label}
                        </a>
                      ) : (
                        <Link
                          key={c.label} to={c.to} onClick={handleCollapse}
                          className={`inline-flex items-center justify-center h-10 rounded-full text-[12.5px] font-semibold transition-colors ${
                            c.primary ? 'bg-[#F77F11] hover:bg-[#d8651a] text-white' : 'border border-neutral-300 text-neutral-700 hover:border-neutral-500'
                          }`}
                          data-testid={`concierge-cta-${view}-page`}
                        >
                          {c.label}
                        </Link>
                      ),
                    )}
                  </div>
                  <button
                    type="button" onClick={resetMenu}
                    className="mt-3 inline-flex items-center gap-1 text-[11px] text-neutral-400 hover:text-neutral-700 transition-colors"
                    data-testid="concierge-back"
                  >
                    <ArrowLeft className="w-3 h-3" /> {isEs ? 'No soy esto — cambiar' : "Not me — change"}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <Link
              to="/operating-intelligence" onClick={handleCollapse}
              className="block border-t border-neutral-100 py-2.5 px-4 text-center text-[9px] tracking-[0.12em] uppercase font-bold text-neutral-400 hover:text-[#F77F11] transition-colors truncate"
              data-testid="concierge-footer-link"
            >
              {isEs ? 'Operado por el Equipo Kitxens' : 'Powered by the Kitxens Operating Team'}
            </Link>
          </motion.div>
        ) : (
          <motion.button
            key="fab" type="button"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.8 }}
            animate={reduceMotion ? {} : { opacity: 1, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            whileHover={reduceMotion ? {} : { scale: 1.06 }}
            whileTap={reduceMotion ? {} : { scale: 0.94 }}
            onClick={handleExpand} onMouseEnter={handleExpand} onFocus={handleExpand}
            aria-label={isEs ? 'Hablar con Rachel' : 'Talk to Rachel'}
            className="pointer-events-auto relative w-14 h-14 rounded-full bg-[#25D366] text-white shadow-[0_20px_48px_-14px_rgba(37,211,102,0.7)] flex items-center justify-center"
            data-testid="concierge-fab"
          >
            <span aria-hidden="true" className="kx-fab-pulse absolute inset-0 rounded-full bg-[#25D366]" style={{ animation: 'kx-concierge-pulse 25s ease-out infinite' }} />
            <WhatsAppGlyph className="w-6 h-6 relative" />
            <span aria-hidden="true" className="absolute -top-1 -right-1 w-5 h-5 rounded-full overflow-hidden ring-2 ring-white shadow-[0_4px_10px_-4px_rgba(15,17,23,0.5)] bg-orange-50">
              <img src={rachel.img} alt="" loading="lazy" className="w-full h-full object-cover" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
